import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Phone, Mail, MapPin, Clock, ExternalLink } from "lucide-react";
import { DIVISIONS } from "@/constants/site";

export default function Footer({ settings }) {
  const s = settings || {};
  const gbpUrl = s.gbp_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || "SSK Enterprises Hyderabad")}`;
  const emailAddr = s.email || "sales@ssk21.net";
  const emailHref = `mailto:${emailAddr}?subject=${encodeURIComponent("Product Enquiry – SSK Enterprises")}`;
  return (
    <footer className="bg-[#0F1E3B] text-slate-300" data-testid="site-footer">
      <div className="container-x py-16 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="w-[64px] h-[64px] bg-[#B5121B] text-white flex items-center justify-center font-display font-black text-2xl rounded-sm flex-shrink-0">SSK</div>
            <div>
              <div className="font-display font-black text-white text-2xl leading-tight">SSK Enterprises</div>
              <div className="text-[11px] tracking-[0.12em] uppercase text-slate-400 font-semibold mt-0.5">Authorised Distributor of 3AK Chemie Pvt. Ltd.</div>
            </div>
          </div>
          <p className="text-sm text-slate-400 mt-4 leading-relaxed max-w-md">
            Authorised Distributor of 3AK Chemie Pvt. Ltd. for MR Chemie NDT materials and inspection solutions. We also supply KLYDE cold galvanizing zinc products, industrial bearings and BESSEY clamping and cutting tools across Telangana and Andhra Pradesh.
          </p>
          <div className="mt-6 border border-slate-800 px-4 py-3 max-w-xs" data-testid="footer-business-hours">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-[#B5121B]" />
              <div className="text-[10px] font-mono-tech tracking-[0.18em] uppercase text-slate-400">Business Hours</div>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between gap-4 text-slate-200">
                <span>Mon – Sat</span>
                <span>9:00 AM – 7:00 PM</span>
              </div>
              <div className="flex justify-between gap-4 text-slate-500">
                <span>Sunday</span>
                <span>Closed</span>
              </div>
            </div>
          </div>
          <a href={gbpUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 border border-slate-700 hover:border-white hover:text-white text-xs px-3 py-2" data-testid="footer-gbp">
            <MapPin className="w-4 h-4 text-[#B5121B]" /> Open on Google Maps <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <div>
          <div className="text-[11px] font-mono-tech uppercase tracking-[0.18em] text-slate-400 mb-4">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-white">Home</Link></li>
            <li><Link to="/about" className="hover:text-white">About Us</Link></li>
            <li><Link to="/industries" className="hover:text-white">Industries</Link></li>
            <li><Link to="/certificates" className="hover:text-white">Certificates</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-[11px] font-mono-tech uppercase tracking-[0.18em] text-slate-400 mb-4">Divisions</div>
          <ul className="space-y-2 text-sm">
            {["ndt_materials", "ndt_equipment", "klyde_zinc", "bessey", "bearings"].map((k) => {
              const d = DIVISIONS[k];
              if (!d) return null;
              return <li key={d.key}><Link to={`/products/${d.slug}`} className="hover:text-white">{d.label}</Link></li>;
            })}
          </ul>
        </div>

        <div>
          <div className="text-[11px] font-mono-tech uppercase tracking-[0.18em] text-slate-400 mb-4">Contact</div>
          <ul className="space-y-3 text-sm">
            {s.phone && (
              <li className="flex gap-2" data-testid="footer-call">
                <Phone className="w-4 h-4 mt-0.5 text-[#B5121B]" />
                <div>
                  <div className="text-[10px] font-mono-tech tracking-[0.14em] uppercase text-slate-500">Calls</div>
                  <a href={`tel:${s.phone.replace(/\s/g,"")}`} className="hover:text-white">{s.phone}</a>
                </div>
              </li>
            )}
            {s.whatsapp && (
              <li className="flex gap-2" data-testid="footer-whatsapp">
                <Phone className="w-4 h-4 mt-0.5 text-[#B5121B]" />
                <div>
                  <div className="text-[10px] font-mono-tech tracking-[0.14em] uppercase text-slate-500">WhatsApp</div>
                  <a href={`https://wa.me/${s.whatsapp.replace(/[^0-9]/g,"")}`} target="_blank" rel="noreferrer" className="hover:text-white">{s.whatsapp}</a>
                </div>
              </li>
            )}
            {s.phone_sales && (
              <li className="flex gap-2" data-testid="footer-additional">
                <Phone className="w-4 h-4 mt-0.5 text-slate-500" />
                <div>
                  <div className="text-[10px] font-mono-tech tracking-[0.14em] uppercase text-slate-500">Additional</div>
                  <span className="text-slate-300">{s.phone_sales}</span>
                </div>
              </li>
            )}
            <li className="flex gap-2"><Mail className="w-4 h-4 mt-0.5 text-[#B5121B]" /><a href={emailHref} className="hover:text-white" data-testid="footer-email">{emailAddr}</a></li>
            <li className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#B5121B] flex-shrink-0" /><address className="not-italic leading-relaxed">{s.address || "Hyderabad, Telangana, India"}</address></li>
            <li className="flex gap-2 pt-2">
              <a href={s.linkedin || "#"} target="_blank" rel="noreferrer" className="p-2 border border-slate-700 hover:border-white hover:text-white" data-testid="linkedin-link"><Linkedin className="w-4 h-4" /></a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="container-x py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} SSK Enterprises. All rights reserved.{s.gst && s.gst !== "GSTIN-XXXXXXXXX" ? ` GSTIN: ${s.gst}` : ""}</div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
